import React from "react";
import {
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Delete as DeleteIcon,
  AutoAwesome as AIIcon,
  HourglassEmpty as LoadingIcon,
} from "@mui/icons-material";

/**
 * Hook to create common DataGrid action configurations
 * This provides pre-configured action objects for common scenarios
 */
const useDataGridActions = () => {
  /**
   * Create a standard Edit action
   */
  const createEditAction = ({
    onClick,
    label = "Edit",
    eventData,
    show = true,
  }) => ({
    key: "edit",
    label,
    icon: <EditIcon fontSize="small" />,
    onClick,
    show,
    eventData,
  });

  /**
   * Create a standard Delete action with confirmation
   */
  const createDeleteAction = ({
    onClick,
    label = "Delete",
    title = "Delete this item?",
    message = "This action cannot be undone.",
    confirmLabel = "Yes",
    cancelLabel = "No",
    eventData,
    cancelEventData,
    show = true,
    divider = true,
  }) => ({
    key: "delete",
    label,
    icon: <DeleteIcon fontSize="small" color="error" />,
    onClick,
    show,
    destructive: true,
    divider,
    confirm: {
      title,
      message,
      confirmLabel,
      cancelLabel,
      cancelEventData,
    },
    eventData,
  });

  /**
   * Create a standard Duplicate/Copy action
   */
  const createDuplicateAction = ({
    onClick,
    label = "Duplicate",
    eventData,
    show = true,
  }) => ({
    key: "duplicate",
    label,
    icon: <FileCopyIcon fontSize="small" />,
    onClick,
    show,
    eventData,
  });

  /**
   * Create actions for the Labels admin page
   */
  const createLabelActions = ({
    onEdit,
    onDelete,
    pageName = "Admin",
    tabName = "Labels",
  }) => [
    createEditAction({
      onClick: onEdit,
      eventData: {
        buttonName: "Edit Label",
        pageName,
        tabName,
      },
    }),
    createDeleteAction({
      onClick: onDelete,
      title: "Delete this label?",
      message:
        "This cannot be undone. The label will be removed everywhere it is being used.",
      eventData: {
        buttonName: "Delete Label",
        pageName,
        tabName,
      },
      cancelEventData: {
        buttonName: "Cancel Label Delete",
        pageName,
        tabName,
      },
    }),
  ];

  /**
   * Create actions for the Protocol table
   */
  const createProtocolActions = ({ onEdit, onDuplicate, onDelete }) => [
    createEditAction({
      onClick: onEdit,
      eventData: {
        buttonName: "Edit Protocol",
        pageName: "Protocols",
      },
    }),
    createDuplicateAction({
      onClick: onDuplicate,
      eventData: {
        buttonName: "Duplicate Protocol",
        pageName: "Protocols",
      },
    }),
    createDeleteAction({
      onClick: onDelete,
      title: "Delete this training plan?",
      message: "This cannot be undone. Continue with delete?",
      eventData: {
        buttonName: "Delete Protocol",
        pageName: "Protocols",
      },
      cancelEventData: {
        buttonName: "Cancel Protocol Delete",
        pageName: "Protocols",
      },
    }),
  ];

  /**
   * Create actions for admin pages with only Edit functionality
   */
  const createEditOnlyActions = ({
    onEdit,
    events,
    entityName,
    pageName = "Admin",
    tabName,
  }) => [
    createEditAction({
      onClick: onEdit,
      eventData: events
        ? {
            buttonName: `Edit ${entityName}`,
            pageName,
            tabName,
          }
        : undefined,
    }),
  ];

  /**
   * Create actions for ingredients with Edit and AI Nutrition
   */
  const createIngredientActions = ({
    onEdit,
    onAINutrition,
    events,
    entityName = "Ingredient",
    pageName = "Nutrition",
    tabName = "Ingredients",
    isAiLoading = false,
  }) => {
    console.log("createIngredientActions called with isAiLoading:", isAiLoading);
    return [
      createEditAction({
        onClick: onEdit,
        eventData: events
          ? {
              buttonName: `Edit ${entityName}`,
              pageName,
              tabName,
            }
          : undefined,
      }),
      {
        key: "ai-nutrition",
        label: isAiLoading ? "AI Working..." : "Fill Nutrition with AI",
        icon: isAiLoading ? <LoadingIcon fontSize="small" /> : <AIIcon fontSize="small" />,
        onClick: isAiLoading ? null : onAINutrition,
        show: true,
        disabled: isAiLoading,
        eventData: events && !isAiLoading
          ? {
              buttonName: `AI Fill Nutrition`,
              pageName,
              tabName,
            }
          : undefined,
      },
    ];
  };

  /**
   * Create actions for admin pages with Edit and Delete
   */
  const createEditDeleteActions = ({
    onEdit,
    onDelete,
    events,
    entityName,
    deleteCondition,
    pageName = "Admin",
    tabName,
    customDeleteMessage,
  }) => [
    createEditAction({
      onClick: onEdit,
      eventData: events
        ? {
            buttonName: `Edit ${entityName}`,
            pageName,
            tabName,
          }
        : undefined,
    }),
    createDeleteAction({
      onClick: onDelete,
      title: `Delete this ${entityName.toLowerCase()}?`,
      message: customDeleteMessage || "This action cannot be undone.",
      show: deleteCondition !== undefined ? deleteCondition : true,
      eventData: events
        ? {
            buttonName: `Delete ${entityName}`,
            pageName,
            tabName,
          }
        : undefined,
      cancelEventData: events
        ? {
            buttonName: `Cancel ${entityName} Delete`,
            pageName,
            tabName,
          }
        : undefined,
    }),
  ];

  return {
    createEditAction,
    createDeleteAction,
    createDuplicateAction,
    createLabelActions,
    createProtocolActions,
    createEditOnlyActions,
    createEditDeleteActions,
    createIngredientActions,
  };
};

export default useDataGridActions;
